import { useCallback, useEffect, useRef, useState } from 'react';

import type { MomentsReflectionSourceIdentity } from '@/store/api/momentsApi';
import type { RefObject } from 'react';

export interface ArtifactTextSelection {
  absTop: number;
  blockId: string;
  endIndex: number;
  startIndex: number;
  text: string;
  sourceIdentity?: MomentsReflectionSourceIdentity;
}

export interface ArtifactSelectionButtonPosition {
  bottom: number;
  right: number;
  top: number;
}

interface ReadArtifactTextSelectionOptions {
  minSelectionLength?: number;
  root: ParentNode;
}

interface UseArtifactTextSelectionArgs {
  contentRef: RefObject<HTMLElement>;
  enabled: boolean;
  minSelectionLength?: number;
  onSelection?: (selection: ArtifactTextSelection) => boolean | void;
  projectSelection?: (
    selection: Selection | null,
    root: HTMLElement,
  ) => ArtifactTextSelection | null;
}

const defaultMinSelectionLength = 5;

const isElement = (node: Node | null): node is HTMLElement =>
  node instanceof HTMLElement;

const nodeToElement = (node: Node | null): HTMLElement | null => {
  if (!node) {
    return null;
  }
  if (isElement(node)) {
    return node;
  }
  return node.parentElement;
};

const findBlockElement = (
  node: Node | null,
  root: ParentNode,
): HTMLElement | null => {
  let current = nodeToElement(node);

  while (current) {
    if (!root.contains(current)) {
      return null;
    }
    if (current.dataset.blockId) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
};

const getBlockTextLength = (element: HTMLElement): number =>
  element.textContent?.length ?? 0;

const getRangeOffsetWithinBlock = (
  blockElement: HTMLElement,
  container: Node,
  offset: number,
): number => {
  const range = document.createRange();
  range.selectNodeContents(blockElement);
  range.setEnd(container, offset);
  return range.toString().length;
};

const getOrderedBlockElements = (root: ParentNode): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>('[data-block-id]'));

const resolveCrossBlockEndpoint = ({
  endBlockElement,
  endIndex,
  root,
  startBlockElement,
  startIndex,
  textLength,
}: {
  endBlockElement: HTMLElement;
  endIndex: number;
  root: ParentNode;
  startBlockElement: HTMLElement;
  startIndex: number;
  textLength: number;
}): { blockId: string; endIndex: number } => {
  const startBlockId = startBlockElement.dataset.blockId ?? '';
  const endBlockId = endBlockElement.dataset.blockId ?? '';

  if (endIndex > 0) {
    return { blockId: `${startBlockId}:${endBlockId}`, endIndex };
  }

  const allBlockElements = getOrderedBlockElements(root);
  const endBlockPosition = allBlockElements.findIndex(
    (element) => element.dataset.blockId === endBlockId,
  );
  const previousBlockElement =
    endBlockPosition > 0 ? allBlockElements[endBlockPosition - 1] : null;

  if (!previousBlockElement?.dataset.blockId) {
    return {
      blockId: startBlockId,
      endIndex: Math.min(
        startIndex + textLength,
        getBlockTextLength(startBlockElement),
      ),
    };
  }

  if (previousBlockElement.dataset.blockId === startBlockId) {
    return {
      blockId: startBlockId,
      endIndex: getBlockTextLength(startBlockElement),
    };
  }

  return {
    blockId: `${startBlockId}:${previousBlockElement.dataset.blockId}`,
    endIndex: getBlockTextLength(previousBlockElement),
  };
};

export const readArtifactTextSelection = (
  selection: Selection | null,
  {
    minSelectionLength = defaultMinSelectionLength,
    root,
  }: ReadArtifactTextSelectionOptions,
): ArtifactTextSelection | null => {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }

  const rawText = selection.toString();
  const text = rawText.trim();
  if (text.length < minSelectionLength) {
    return null;
  }
  const leadingTrimLength = rawText.length - rawText.trimStart().length;
  const trailingTrimLength = rawText.length - rawText.trimEnd().length;

  const range = selection.getRangeAt(0);
  const startBlockElement = findBlockElement(range.startContainer, root);
  if (!startBlockElement?.dataset.blockId) {
    return null;
  }

  const endBlockElement = findBlockElement(range.endContainer, root);
  const rawStartIndex = getRangeOffsetWithinBlock(
    startBlockElement,
    range.startContainer,
    range.startOffset,
  );
  const startIndex = Math.min(
    rawStartIndex + leadingTrimLength,
    getBlockTextLength(startBlockElement),
  );

  let { blockId } = startBlockElement.dataset;
  let endIndex: number;

  if (
    endBlockElement?.dataset.blockId &&
    endBlockElement !== startBlockElement
  ) {
    const rawEndIndex = getRangeOffsetWithinBlock(
      endBlockElement,
      range.endContainer,
      range.endOffset,
    );
    const adjustedEndIndex = Math.max(0, rawEndIndex - trailingTrimLength);
    const resolved = resolveCrossBlockEndpoint({
      endBlockElement,
      endIndex: adjustedEndIndex,
      root,
      startBlockElement,
      startIndex,
      textLength: text.length,
    });
    blockId = resolved.blockId;
    endIndex = resolved.endIndex;
  } else if (endBlockElement === startBlockElement) {
    const rawEndIndex = getRangeOffsetWithinBlock(
      startBlockElement,
      range.endContainer,
      range.endOffset,
    );
    endIndex = Math.max(startIndex, rawEndIndex - trailingTrimLength);
  } else {
    endIndex = getBlockTextLength(startBlockElement);
  }

  if (endIndex <= startIndex && !blockId.includes(':')) {
    return null;
  }

  const rect = range.getBoundingClientRect();
  return {
    absTop: rect.top + window.scrollY,
    blockId,
    endIndex,
    startIndex,
    text,
  };
};

export const useArtifactTextSelection = ({
  contentRef,
  enabled,
  minSelectionLength = defaultMinSelectionLength,
  onSelection,
  projectSelection,
}: UseArtifactTextSelectionArgs) => {
  const [buttonPosition, setButtonPosition] =
    useState<ArtifactSelectionButtonPosition | null>(null);
  const selectionRef = useRef<ArtifactTextSelection | null>(null);
  // A clone of the selected range, independent of the live browser selection,
  // so the button can re-measure it on scroll after focus moves to the button.
  // The page scrolls an inner layout element, not the window, so
  // window.scrollY cannot locate the selection.
  const selectionRangeRef = useRef<Range | null>(null);
  const deferredSelectionUpdateRefs = useRef<number[]>([]);

  const clearSelection = useCallback(() => {
    selectionRef.current = null;
    selectionRangeRef.current = null;
    setButtonPosition(null);
  }, []);

  const confirmSelection = useCallback(() => {
    const selection = selectionRef.current;
    if (!selection) {
      return null;
    }
    const selectionWasAccepted = onSelection?.(selection) !== false;
    if (!selectionWasAccepted) {
      return null;
    }

    clearSelection();
    window.getSelection()?.removeAllRanges();
    return selection;
  }, [clearSelection, onSelection]);

  const updateSelectionFromBrowser = useCallback(() => {
    if (!enabled || !contentRef.current) {
      clearSelection();
      return;
    }

    const browserSelection = window.getSelection();
    const selection = projectSelection
      ? projectSelection(browserSelection, contentRef.current)
      : readArtifactTextSelection(browserSelection, {
          minSelectionLength,
          root: contentRef.current,
        });

    if (!selection || !browserSelection || browserSelection.rangeCount === 0) {
      clearSelection();
      return;
    }

    const range = browserSelection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    selectionRef.current = selection;
    selectionRangeRef.current = range.cloneRange();
    setButtonPosition({
      bottom: rect.bottom,
      right: rect.right,
      top: rect.top,
    });
  }, [
    clearSelection,
    contentRef,
    enabled,
    minSelectionLength,
    projectSelection,
  ]);

  useEffect(() => {
    const clearDeferredSelectionUpdates = () => {
      for (const timeoutId of deferredSelectionUpdateRefs.current) {
        window.clearTimeout(timeoutId);
      }
      deferredSelectionUpdateRefs.current = [];
    };

    const runDeferredSelectionUpdate = () => {
      clearDeferredSelectionUpdates();
      for (const delay of [80, 250, 500]) {
        const timeoutId = window.setTimeout(() => {
          deferredSelectionUpdateRefs.current =
            deferredSelectionUpdateRefs.current.filter(
              (currentTimeoutId) => currentTimeoutId !== timeoutId,
            );
          updateSelectionFromBrowser();
        }, delay);
        deferredSelectionUpdateRefs.current.push(timeoutId);
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (
        !event.shiftKey ||
        ![
          'ArrowDown',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'End',
          'Home',
        ].includes(event.key)
      ) {
        return;
      }

      updateSelectionFromBrowser();
    };

    document.addEventListener('mouseup', updateSelectionFromBrowser);
    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('selectionchange', runDeferredSelectionUpdate);
    document.addEventListener('pointerup', runDeferredSelectionUpdate, {
      passive: true,
    });
    document.addEventListener('touchend', runDeferredSelectionUpdate, {
      passive: true,
    });
    return () => {
      clearDeferredSelectionUpdates();
      document.removeEventListener('mouseup', updateSelectionFromBrowser);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener(
        'selectionchange',
        runDeferredSelectionUpdate,
      );
      document.removeEventListener('pointerup', runDeferredSelectionUpdate);
      document.removeEventListener('touchend', runDeferredSelectionUpdate);
    };
  }, [updateSelectionFromBrowser]);

  useEffect(() => {
    if (!enabled) {
      clearSelection();
    }
  }, [clearSelection, enabled]);

  return {
    buttonPosition,
    clearSelection,
    confirmSelection,
    selection: selectionRef.current,
    selectionRange: selectionRangeRef.current,
  };
};
